// Who a bulk admin email may go to, and what the recipient will actually read.
//
// Browser-free so the unit tier covers it. The server decides this again at
// send time — netlify/functions/admin-send-bulk-background.js re-reads every
// opt-out just before sending — because a screen that has been open while a
// newsletter was written is a screen that can be several minutes out of date.
// What lives here is the same judgement made early, so the admin can see who is
// being left out before pressing Send rather than afterwards.

import type { AdminUserEntry } from './adminUserView';

export type MailingList = 'newsletter' | 'checkins' | 'announcements';

// Named ...KEYS rather than MAILING_LISTS so it cannot be mistaken for the
// server-side MAILING_LISTS in netlify/lib/mailingLists.js, which is an object
// keyed by these values and holds what each list actually means.
export const MAILING_LIST_KEYS: MailingList[] = ['newsletter', 'announcements', 'checkins'];

export const MAILING_LIST_LABELS: Record<MailingList, string> = {
  newsletter: 'Newsletter',
  announcements: 'Announcement',
  checkins: 'Check-in email',
};

export const MAILING_LIST_BLURBS: Record<MailingList, string> = {
  newsletter:
    'The monthly email. Carries the newsletter unsubscribe link and a postal address, and is governed by its own opt-out.',
  announcements:
    'A one-off note about TrueLoad itself. Carries a postal address and its own opt-out, so it still reaches somebody who has left the newsletter, and leaving it does not touch the check-in nudges.',
  checkins:
    'The same list as the one-to-one nudges about somebody’s own job. Unsubscribing from this does not touch the newsletter or announcements — but somebody who wanted out of a broadcast will have switched off the nudges that recover their stalled job.',
};

/**
 * The user_profiles column each list's opt-out is recorded in.
 *
 * Exported so the unit tier can check it against `column` in
 * netlify/lib/mailingLists.js, which is the value the sender actually reads.
 */
export const OPT_OUT_FIELD: Record<MailingList, keyof AdminUserEntry> = {
  newsletter: 'newsletter_optout_at',
  announcements: 'announcement_optout_at',
  checkins: 'email_optout_at',
};

/**
 * Which lists carry a postal address in the footer.
 *
 * The sender decides this again from requiresPostalAddress in
 * netlify/lib/mailingLists.js, which is the value that actually ships. This
 * copy exists so the admin preview shows the footer that will really go out
 * without the browser bundle having to import a Netlify function's library.
 * Same arrangement as OPT_OUT_FIELD above, which mirrors that file's `column`.
 */
export const LIST_CARRIES_POSTAL_ADDRESS: Record<MailingList, boolean> = {
  newsletter: true,
  announcements: true,
  checkins: false,
};

/** Why somebody on this list cannot be mailed, in words fit for the screen. */
const OPT_OUT_REASON: Record<MailingList, string> = {
  newsletter: 'Unsubscribed from the newsletter',
  announcements: 'Unsubscribed from announcements',
  checkins: 'Unsubscribed from check-in email',
};

export interface Mailability {
  mailable: boolean;
  /** Why not, in words fit for the screen. Null when they are mailable. */
  reason: string | null;
}

const MAILABLE: Mailability = { mailable: true, reason: null };

export function mailability(user: AdminUserEntry, list: MailingList): Mailability {
  if (!user.email) return { mailable: false, reason: 'No email address on file' };
  if (user.deleted_at) return { mailable: false, reason: 'Account deleted' };
  if (user[OPT_OUT_FIELD[list]]) {
    return { mailable: false, reason: OPT_OUT_REASON[list] };
  }
  return MAILABLE;
}

/** Everyone who could receive this list, whether or not Select all picks them. */
export function mailableUsers(users: AdminUserEntry[], list: MailingList): AdminUserEntry[] {
  return users.filter(u => mailability(u, list).mailable);
}

/**
 * Whether this row's checkbox can be ticked at all.
 *
 * Deliberately says nothing about either opt-out, because the list is chosen
 * after the selection is made: someone who has left the newsletter is still a
 * perfectly good recipient for a check-in. Only the two conditions that hold
 * whatever list is picked — no address, and a deleted account — disable a row.
 * The chosen list is applied later, by summarizeSelection, where the reader can
 * see who it dropped and why.
 */
export function isSelectable(user: AdminUserEntry): boolean {
  return Boolean(user.email) && !user.deleted_at;
}

/**
 * What Select all ticks.
 *
 * Test accounts are left out — nobody means to put a fixture on the newsletter —
 * but they stay tickable by hand, because a test account is exactly what you
 * want to send to when you are checking that sending works. That is the same
 * distinction excluded_from_digest has always drawn.
 */
export function defaultSelection(users: AdminUserEntry[]): string[] {
  return users.filter(u => isSelectable(u) && !u.excluded_from_digest).map(u => u.id);
}

export interface SelectionSummary {
  /** Selected users who will be sent to. */
  sending: AdminUserEntry[];
  /** Selected users who will not be, and why. */
  skipping: { user: AdminUserEntry; reason: string }[];
}

export function summarizeSelection(
  users: AdminUserEntry[],
  selectedIds: Set<string> | string[],
  list: MailingList
): SelectionSummary {
  const ids = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  const sending: AdminUserEntry[] = [];
  const skipping: { user: AdminUserEntry; reason: string }[] = [];

  for (const user of users) {
    if (!ids.has(user.id)) continue;
    const verdict = mailability(user, list);
    if (verdict.mailable) sending.push(user);
    else skipping.push({ user, reason: verdict.reason as string });
  }

  return { sending, skipping };
}

/**
 * The greeting name for one recipient.
 *
 * Falls back to "there" so a missing name reads as "Hi there," rather than as a
 * gap where somebody's name should be — the single most obvious tell that an
 * email was generated rather than written.
 */
export function firstNameOf(fullName: string | null): string {
  const first = (fullName || '').trim().split(/\s+/)[0] ?? '';
  if (!first) return 'there';
  // Names are stored however the user typed them at signup, and at least one is
  // all lowercase. Only the leading character is touched, so JoAnn and McCune
  // survive intact. Same rule as AdminEmailModal's one-to-one drafts.
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/**
 * The one supported token. Kept to a single substitution deliberately: an admin
 * can hold one token in their head while writing, whereas a template language
 * is something that fails silently in front of the whole list.
 *
 * The same replacement runs on the server in
 * netlify/functions/admin-send-bulk-background.js. If this pattern changes,
 * change it there too — the preview would otherwise show something the
 * recipient never receives.
 */
export const FIRST_NAME_TOKEN = /\{\{\s*first_name\s*\}\}/g;

export function renderTemplate(template: string, fullName: string | null): string {
  return template.replace(FIRST_NAME_TOKEN, firstNameOf(fullName));
}
